import { useCallback, useEffect, useRef } from 'react';

import useLabelRegenerationProgress from 'common/hooks/useLabelRegenerationProgress';

import { labelRegenerationStatusesApi } from 'api/requests';

/**
 * POM-6010 — wait for the label regeneration that `commitBulkLabelLotNdc` queued for us.
 *
 * The mutation used to leave regeneration to its caller, which meant a browser that died between
 * the two calls left a fill carrying a new NDC and lot behind a label still showing the old one,
 * with nothing to repair it. Core now queues the work inside the commit, so this hook waits for a
 * run it did not start rather than driving one.
 *
 * The socket is the answer. Core publishes one LabelsRegenerated event per label as it commits and
 * a LabelsRegenerationCompleted for the run — the same events the bulk override flow already
 * relies on. It is the fast path, the only one that can drive a determinate bar, and the only one
 * that reports how many labels actually failed.
 *
 * `labelRegenerationStatuses` is a fallback, not a second opinion. It reads the LABEL_REGENERATING
 * tag core wrote before dispatching, and it exists for one case: the relay dropped an event, or
 * this tab never joined the room, and there is no other id-scoped way to ask. So it stays silent
 * for a minute — long enough that a healthy socket has already settled the wait — and only then
 * starts asking, every 30 seconds. A run that finishes normally never sends a single request.
 */

/**
 * How long the socket gets on its own.
 *
 * Not an estimate of how long regeneration takes — it is how long we are willing to wait before
 * concluding the socket is not going to answer at all. Polling underneath a working socket is
 * pure load, and the load is per user standing at a print dialog, so the cheap thing to do is
 * nothing.
 */
const POLL_START_DELAY_MS = 60000;

/** Gap between polls, once the socket has had its minute. */
const POLL_INTERVAL_MS = 30000;

/**
 * Gap between polls when there is no socket at all.
 *
 * The cadence above is a fallback's cadence: the socket has already failed to answer for a minute
 * and a coarse check is enough to catch up. With no socket this query is the only channel there
 * will ever be, and it is carrying a user who is standing in front of a print dialog — so it runs
 * at a pace that can actually track the work rather than reporting it up to half a minute late.
 */
const NO_SOCKET_POLL_INTERVAL_MS = 5000;

/**
 * The backstop, for the case where BOTH answers fail: a batch stuck behind other SQS traffic, or
 * one whose chunks are on their way to the DLQ (which takes ~25 minutes, far longer than anyone
 * will hold this dialog open). Deliberately far shorter than the hook's own 15-minute queued
 * default — a print dialog that sits spinning for a quarter of an hour is not a fallback, it is a
 * hang. On expiry the caller reports what is still pending and lets the user retry or print anyway.
 */
const WAIT_TIMEOUT_MS = 300000;

const REGENERATING = 'REGENERATING';

const useCommittedLabelRegeneration = () => {
  const progress = useLabelRegenerationProgress({ queuedTimeoutMs: WAIT_TIMEOUT_MS });

  const pollRef = useRef(null);
  const pollDelayRef = useRef(null);
  // Held in a ref so the poll and the socket cannot both settle the same wait.
  const settleRef = useRef(null);

  // Read from inside the poll, which holds the closure it was scheduled with. The socket can drop
  // after the wait begins, and what the poll needs to know is whether it is live NOW.
  const isRealtimeAvailableRef = useRef(progress.isRealtimeAvailable);
  isRealtimeAvailableRef.current = progress.isRealtimeAvailable;

  const stopPolling = useCallback(() => {
    if (pollDelayRef.current) {
      clearTimeout(pollDelayRef.current);
      pollDelayRef.current = null;
    }

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  /**
   * Resolves once the labels for `requestLineItemIds` are rebuilt, or the wait gives up.
   *
   * Never rejects: a failed poll is not a failed regeneration, and the caller has already
   * committed by the time it gets here. `pending` is what could not be confirmed — empty on the
   * happy path, and the list the caller warns about otherwise.
   */
  const waitForCommittedLabels = useCallback(
    (requestLineItemIds) =>
      new Promise((resolve) => {
        const total = requestLineItemIds.length;

        if (total === 0) {
          resolve({ completed: 0, total: 0, pending: [], timedOut: false });

          return;
        }

        settleRef.current = (outcome) => {
          settleRef.current = null;
          stopPolling();
          resolve(outcome);
        };

        const settle = (outcome) => settleRef.current?.(outcome);

        progress.startTracking({
          requestLineItemIds,
          // Core told us it queued; the LabelsRegenerationQueued event fired while the commit
          // mutation was still open, so we were not listening for it yet.
          queued: true,
          onComplete: ({ completed, failed = 0 }) =>
            settle({
              completed,
              total,
              // The server's own count is authoritative on the queued branch, but it does not say
              // WHICH labels failed. Naming none of them is better than naming the wrong ones —
              // the caller's message degrades to a count.
              pending: [],
              failed,
              timedOut: false,
            }),
          onTimeout: ({ completed }) => {
            /**
             * Every label we submitted reported in over the socket and only the server's own
             * completion event is missing — this is the grace window expiring, not the run
             * failing. Warning about labels we watched rebuild one by one would be false.
             */
            if (completed >= total) {
              settle({ completed, total, pending: [], timedOut: false });

              return;
            }

            settle({ completed, total, pending: requestLineItemIds, timedOut: true });
          },
        });

        /**
         * The poll may only report completion once something has PROVED the run is real.
         *
         * core's setLabelRegeneratingTag swallows its own failures — the regeneration still runs
         * if tagging fails, and an untagged run looks exactly like a finished one. Reading that as
         * done would let the user print the previous lot, which is the bug this screen exists to
         * prevent. Three things count as proof, and any one of them is enough:
         *
         * - the poll itself saw a row still tagged;
         * - the socket delivered at least one label of this batch, so the run demonstrably ran and
         *   its tags are simply already cleared;
         * - there is no socket at all, in which case no corroboration can ever arrive and the poll
         *   is the only answer the user is ever going to get.
         *
         * With none of them the wait is left to the timeout, which reports the labels as
         * unconfirmed rather than silently blessing them.
         */
        let sawPending = false;

        const poll = async () => {
          try {
            const statuses = await labelRegenerationStatusesApi(requestLineItemIds);
            const pending = statuses
              .filter((row) => row.labelRegenerationStatus === REGENERATING)
              .map((row) => row.requestLineItemId);

            if (pending.length > 0) {
              sawPending = true;

              return;
            }

            const ran =
              sawPending || progress.getCompletedCount() > 0 || !isRealtimeAvailableRef.current;

            if (ran) {
              settle({ completed: total, total, pending: [], timedOut: false });
            }
          } catch {
            // A failed poll says nothing about the regeneration. Keep going — the socket and the
            // timeout are both still live.
          }
        };

        /* Nothing is asked for the first minute — a working socket settles the wait and clears
         * this timer long before it fires. With no socket there is nothing to defer to: this query
         * IS the primary path, so it starts at once and runs at a pace that can track the work. */
        const hasSocket = isRealtimeAvailableRef.current;

        pollDelayRef.current = setTimeout(
          () => {
            pollDelayRef.current = null;
            poll();
            pollRef.current = setInterval(
              poll,
              hasSocket ? POLL_INTERVAL_MS : NO_SOCKET_POLL_INTERVAL_MS
            );
          },
          hasSocket ? POLL_START_DELAY_MS : 0
        );
      }),
    [progress, stopPolling]
  );

  return {
    waitForCommittedLabels,
    // For the caller's progress bar. Zero-safe when nothing is being tracked.
    isTracking: progress.isTracking,
    completedCount: progress.completedCount,
    total: progress.total,
    percent: progress.percent,
    isRealtimeAvailable: progress.isRealtimeAvailable,
  };
};

export default useCommittedLabelRegeneration;
